import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Eye, Loader2, Plus, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import type { DocumentComplianceType, EmployeeDocument } from '@ip/shared-types';
import { DOCUMENT_COMPLIANCE_TYPES } from '@ip/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DatePicker } from '@/components/shared/DatePicker';
import { SignatureBadges } from '@/components/documents/SignatureBadges';
import { PublicationDetailSheet } from '@/components/documents/PublicationDetailSheet';
import { useDocumentsApi, useEmployeesApi } from '@/services/api';
import { sanitizeFileNamePart } from '@/utils/download';
import { t } from '@/i18n';

const COLUMN_COUNT = 5;

/** Plain-label document types (no compliance link); 'other' reveals the custom label input. */
const PLAIN_DOC_TYPES = ['umowa', 'aneks', 'certyfikat', 'zaswiadczenie'] as const;

function isComplianceType(v: string): v is DocumentComplianceType {
  return (DOCUMENT_COMPLIANCE_TYPES as readonly string[]).includes(v);
}

interface EmployeeDocumentsProps {
  companyNip: string;
  employeeId: string;
}

function expiryStatus(expiry: string | null): { tag: 'expired' | 'soon' | null; days?: number } {
  if (!expiry) return { tag: null };
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const exp = new Date(expiry + 'T00:00:00Z');
  const days = Math.round((exp.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return { tag: 'expired' };
  if (days <= 30) return { tag: 'soon', days };
  return { tag: null, days };
}

function ExpiryCell({ expiryDate }: { expiryDate: string | null }) {
  const s = expiryStatus(expiryDate);
  if (s.tag === 'expired') {
    return (
      <Badge
        variant="outline"
        className="border-red-500/40 bg-red-500/10 font-normal text-red-700 dark:text-red-400"
      >
        {t('employees.documents.expired')}
      </Badge>
    );
  }
  if (s.tag === 'soon') {
    return (
      <Badge
        variant="outline"
        className="border-amber-500/40 bg-amber-500/10 font-normal text-amber-700 dark:text-amber-400"
      >
        {t('employees.documents.expiryWarning', { days: s.days! })}
      </Badge>
    );
  }
  return <>{expiryDate ?? '—'}</>;
}

export function EmployeeDocuments({ companyNip, employeeId }: EmployeeDocumentsProps) {
  const { listDocuments, addDocument, downloadDocument, removeDocument } = useEmployeesApi();
  const { downloadAcceptanceReturned } = useDocumentsApi();
  const [docs, setDocs] = useState<EmployeeDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState('');
  const [label, setLabel] = useState('');
  const [expiry, setExpiry] = useState<Date | null>(null);
  // Signed publication scans open the full acceptance/countersign progress
  // (who signed, how, who is still missing) instead of only offering the file.
  const [detailPublicationId, setDetailPublicationId] = useState<string | null>(null);

  const refresh = () => {
    let cancelled = false;
    setLoading(true);
    listDocuments(companyNip, employeeId)
      .then((d) => { if (!cancelled) setDocs(d); })
      .catch((err) => { if (!cancelled) toast.error(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  };

  useEffect(refresh, [companyNip, employeeId, listDocuments]);

  const openUpload = () => {
    setDocType('');
    setLabel('');
    setExpiry(null);
    setFile(null);
    setUploadOpen(true);
  };

  const handleDownload = async (row: EmployeeDocument) => {
    try {
      await downloadDocument(
        companyNip,
        employeeId,
        row.id,
        row.fileName || sanitizeFileNamePart(row.label),
      );
    } catch {
      /* session expired — handled by the global modal */
    }
  };

  // Signed publication scans are read-only mirrors: they carry the acceptance id
  // as `id` and go through the publications returned-file endpoint, not the
  // employees file endpoint.
  const handleDownloadSigned = async (row: EmployeeDocument) => {
    try {
      await downloadAcceptanceReturned(
        companyNip,
        row.publicationId!,
        row.id,
        row.fileName || `${sanitizeFileNamePart(row.label)}-podpisany`,
      );
    } catch {
      /* session expired — handled by the global modal */
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await removeDocument(companyNip, employeeId, docId);
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleUpload = async () => {
    if (!docType) {
      toast.error(t('employees.validation.required', { label: t('employees.documents.type') }));
      return;
    }
    const compliance = isComplianceType(docType);
    if (compliance && !expiry) {
      toast.error(t('employees.validation.required', { label: t('employees.documents.expiry') }));
      return;
    }
    if (docType === 'other' && !label.trim()) {
      toast.error(t('employees.validation.required', { label: t('employees.documents.customLabel') }));
      return;
    }
    if (!file) {
      toast.error(t('employees.validation.fileRequired'));
      return;
    }
    const effectiveLabel = compliance
      ? ''
      : docType === 'other'
        ? label.trim()
        : t(`employees.documents.types.${docType}`);
    setBusy(true);
    try {
      await addDocument(
        companyNip,
        employeeId,
        file,
        effectiveLabel,
        expiry ? dayjs(expiry).format('YYYY-MM-DD') : undefined,
        compliance ? docType : undefined,
      );
      setUploadOpen(false);
      setFile(null);
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button onClick={openUpload}>
          <Plus className="size-4" />
          {t('employees.documents.upload')}
        </Button>
      </div>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('employees.documents.label')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('employees.documents.fileName')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[180px] whitespace-nowrap">
                {t('employees.documents.uploadedAt')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[180px] whitespace-nowrap">
                {t('employees.documents.expiry')}
              </TableHead>
              <TableHead className="w-[140px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground h-24 text-center">
                  {t('common.none')}
                </TableCell>
              </TableRow>
            ) : (
              docs.map((row) => {
                const isSigned = row.source === 'publication_signed';
                // Only a signed row carries a publication to drill into.
                const detailId = isSigned ? (row.publicationId ?? null) : null;
                return (
                <TableRow
                  key={row.id}
                  className={detailId ? 'cursor-pointer' : undefined}
                  onClick={detailId ? () => setDetailPublicationId(detailId) : undefined}
                >
                  <TableCell className="py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{row.label}</span>
                      {isSigned && (
                        <>
                          <Badge
                            variant="outline"
                            className="border-primary/30 bg-primary/10 text-primary font-normal"
                          >
                            {t('employees.documents.signedPublicationBadge')}
                          </Badge>
                          {row.acceptanceStatus === 'signed' ? (
                            <Badge
                              variant="outline"
                              className="border-emerald-500/40 bg-emerald-500/10 font-normal text-emerald-700 dark:text-emerald-400"
                            >
                              {t('employees.documents.signedConfirmed')}
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="border-amber-500/40 bg-amber-500/10 font-normal text-amber-700 dark:text-amber-400"
                            >
                              {t('employees.documents.signedAwaitingConfirmation')}
                            </Badge>
                          )}
                        </>
                      )}
                      <SignatureBadges verification={row.signatureVerification} />
                    </div>
                  </TableCell>
                  <TableCell className="py-2">{row.fileName}</TableCell>
                  <TableCell className="py-2">
                    {new Date(row.uploadedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="py-2">
                    <ExpiryCell expiryDate={row.expiryDate} />
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {detailId && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          aria-label={t('employees.documents.signatureDetails')}
                          title={t('employees.documents.signatureDetails')}
                          onClick={() => setDetailPublicationId(detailId)}
                        >
                          <Eye className="size-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label={t('employees.documents.download')}
                        title={t('employees.documents.download')}
                        onClick={() => (isSigned ? handleDownloadSigned(row) : handleDownload(row))}
                      >
                        <Download className="size-4" />
                      </Button>
                      {!isSigned && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive size-8">
                              <Trash2 className="size-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t('employees.documents.deleteConfirm')}
                              </AlertDialogTitle>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-white hover:bg-destructive/90"
                                onClick={() => handleDelete(row.id)}
                              >
                                {t('common.delete')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('employees.documents.upload')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="doc-type">{t('employees.documents.type')}</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger id="doc-type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_COMPLIANCE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`hr.compliance.types.${type}`)}
                    </SelectItem>
                  ))}
                  {PLAIN_DOC_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`employees.documents.types.${type}`)}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">{t('employees.documents.types.other')}</SelectItem>
                </SelectContent>
              </Select>
              {isComplianceType(docType) && (
                <p className="text-muted-foreground text-xs">{t('employees.documents.complianceHint')}</p>
              )}
            </div>
            {docType === 'other' && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="doc-label">{t('employees.documents.customLabel')}</Label>
                <Input id="doc-label" value={label} onChange={(e) => setLabel(e.target.value)} />
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label>
                {t('employees.documents.expiry')}
                {isComplianceType(docType) && <span className="text-destructive"> *</span>}
              </Label>
              <DatePicker value={expiry} onChange={setExpiry} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="doc-file">{t('employees.documents.fileName')}</Label>
              <input
                id="doc-file"
                type="file"
                accept="application/pdf,image/*"
                className="text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={busy} onClick={handleUpload}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PublicationDetailSheet
        nip={companyNip}
        publicationId={detailPublicationId}
        onOpenChange={() => setDetailPublicationId(null)}
        onChanged={refresh}
      />
    </div>
  );
}
